import React, { useState } from 'react';
import styled from 'styled-components';
import { useAppState } from '../../../store/appstate';
import { ConnectedStore } from '../../../types/ConnectedStore';
import CenterContent from '../../atoms/Layout/CenterContent';
import HeaderContent from '../../atoms/Layout/HeaderContent';
import NotAuthorized from '../NotAuthorized';
import SearchForm from './SearchForm';
import SessionUserPermissons from '../../../types/SessionUserPermissions';
import SearchResults from './SearchResults';
import TableHeaderTypes from '../../../types/tableHeaderType';
import Summaries from './Summaries';
import Tabs from '../../atoms/Tabs/Tabs';
import Flex from '../../atoms/Box/Flex';
import PeriodicReports from './PeriodicReports';
import Button from '../../atoms/Button/Button';
import useModal from '../../../services/hooks/useModal';
import { Modal } from '../../atoms/Modal/Modal';
import GeneratePeriodicReport from './GeneratePeriodicReport';
import usePermissions from '../../../services/hooks/usePermissions';
import { SessionOrganization } from '../../../types/SessionUser';
import { getStoresAvailableForUser } from '../../../services/helpers/storeHelper';
import { useAppDispatch } from '../../../store';
import { setTableExpanded } from '../../../store/userSettings/userSettingsSlice';

const Settlements: React.FC = () => {
    const user = useAppState(s => s.session.user);
    const availableStores = useAppState<ConnectedStore[]>(s => s.session.availableStores) ?? [];
    const userFilteredStores = getStoresAvailableForUser(availableStores, user?.filteredStores);

    const permissions = useAppState<SessionUserPermissons[]>(
        s => s.session.user?.permissions || []
    );

    const organization = useAppState<SessionOrganization | undefined>(
        s => s.session.selectedOrganization
    );

    const { hasWrite, hasRead } = usePermissions([
        `all-all-periodicreportdetails`,
        `${organization?.id}-all-periodicreportdetails`,
    ]);

    const [tabState, setTabState] = useState<string>('settlements');
    const { isShown, toggle } = useModal();
    const dispatch = useAppDispatch();
    const isTableExpanded = useAppState(s => s.userSettings.tableExpanded) ?? false;

    if (!permissions.some(x => x.operation === 'read' && x.tag.includes('settlementdetails'))) {
        return (
            <>
                <HeaderContent />
                <CenterContent>
                    <NotAuthorized componentName="Reports" />
                </CenterContent>
            </>
        );
    }

    return (
        <>
            <HeaderContent>
                <CenterContent>
                    {tabState === 'settlements' || tabState === 'summaries' ? (
                        <SearchForm availableStores={userFilteredStores} />
                    ) : (
                        <Flex justifyContent="flex-end" height="7.5rem" alignItems="center">
                            {hasWrite && (
                                <Button
                                    primary
                                    large
                                    onClick={() => toggle()}
                                    data-testid="generate-report-button"
                                >
                                    Generate report
                                </Button>
                            )}
                        </Flex>
                    )}
                </CenterContent>
            </HeaderContent>

            <Page>
                <Flex column rowGap={'2rem'}>
                    <TabsConstraint>
                        <Tabs
                            tabsList={[
                                { key: 'settlements', text: 'Settlements' },
                                { key: 'summaries', text: 'Summaries' },
                                {
                                    key: 'periodicReports',
                                    text: 'Custom Reports',
                                    isVisible: hasRead,
                                },
                            ]}
                            startTab={'settlements'}
                            onTabChange={setTabState}
                        >
                            <SearchResults
                                key={'settlements'}
                                tableHeaderType={TableHeaderTypes.Settlement}
                                availableStores={availableStores}
                                isExpanded={isTableExpanded}
                                onExpandToggle={() => dispatch(setTableExpanded(!isTableExpanded))}
                            />

                            <Summaries key={'summaries'} />
                            <PeriodicReports
                                key={'periodicReports'}
                                isExpanded={isTableExpanded}
                                onExpandToggle={() => dispatch(setTableExpanded(!isTableExpanded))}
                            />
                        </Tabs>
                    </TabsConstraint>
                    <Modal
                        top
                        medium
                        isShown={isShown}
                        hide={() => toggle()}
                        dataTestId="generate-report-modal"
                    >
                        <GeneratePeriodicReport
                            hide={() => toggle()}
                            availableStores={userFilteredStores}
                        />
                    </Modal>
                </Flex>
            </Page>
        </>
    );
};

export default Settlements;

const Page = styled(CenterContent)`
    padding-bottom: 4rem;
`;

const TabsConstraint = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 2rem;
`;
